/**
 * Migration routes: waypoints, endpoints, seasonal panel copy, iNat taxa.
 */

export type EndpointRole = 'winter' | 'breeding' | 'staging' | 'ocean' | 'stopover';

export interface MigrationRouteEndpoint {
  title: string;
  description: string;
  role?: EndpointRole;
}

export interface MigrationRouteDetail {
  funFact: string;
  lore?: string;
  inatTaxonId?: number;
}

export interface MigrationRouteGroup {
  id: string;
  label: string;
  color: string;
  lineWeight?: number;
  waypoints: [number, number][];
  endpoints: {
    start: MigrationRouteEndpoint;
    end: MigrationRouteEndpoint;
  };
  detail?: MigrationRouteDetail;
}

export const MIGRATION_ROUTE_GROUPS: MigrationRouteGroup[] = [
  {
    id: 'gray-whale',
    label: 'Gray whales — Baja → Alaska',
    color: '#5dade2',
    lineWeight: 5,
    waypoints: [
      [27.3, -114.25],
      [28.5, -116.0],
      [32.0, -117.5],
      [40.5, -124.5],
      [49.0, -126.0],
      [55.0, -132.0],
      [58.3, -134.4],
    ],
    endpoints: {
      start: {
        title: 'Gray whales (Baja)',
        description: 'Calving lagoons — warm, protected water. Rest before the longest mammal migration.',
        role: 'breeding',
      },
      end: {
        title: 'Alaska feeding',
        description: 'Summer feeding on amphipods in cold, food-rich water.',
        role: 'ocean',
      },
    },
    detail: {
      funFact: 'Gray whales once called "devil fish" for striking whaling boats — now one of the friendliest whales toward boats in Baja.',
      lore: 'The northbound parade passes California in March–April; locals time whale-watching to this window.',
      inatTaxonId: 85585,
    },
  },
  {
    id: 'monarch',
    label: 'Monarch butterflies — Mexico → US/Canada',
    color: '#e67e22',
    lineWeight: 2,
    waypoints: [
      [19.68, -100.25],
      [22.5, -100.5],
      [28.0, -99.0],
      [35.0, -97.0],
      [40.0, -93.5],
      [43.5, -90.0],
    ],
    endpoints: {
      start: {
        title: 'Monarch overwintering',
        description: 'Oyamel fir forests — millions roost in the same sites for generations.',
        role: 'winter',
      },
      end: {
        title: 'Summer breeding',
        description: 'Milkweed corridor — several generations reproduce moving north.',
        role: 'breeding',
      },
    },
    detail: {
      funFact: 'The butterflies that reach Canada are often 3–4 generations removed from those that left Mexico.',
      inatTaxonId: 48662,
    },
  },
  {
    id: 'hummingbird',
    label: 'Ruby-throated hummingbirds',
    color: '#48c9b0',
    lineWeight: 2,
    waypoints: [
      [18.5, -88.3],
      [21.0, -86.5],
      [26.0, -82.0],
      [30.5, -81.0],
      [35.5, -76.0],
      [40.0, -73.5],
      [44.0, -69.5],
    ],
    endpoints: {
      start: {
        title: 'Winter range',
        description: 'Yucatán & Central America — tropical flowering plants.',
        role: 'winter',
      },
      end: {
        title: 'Breeding range',
        description: 'Eastern US — single 500+ mile Gulf crossing for many individuals.',
        role: 'breeding',
      },
    },
    detail: {
      funFact: 'They can double body weight before the Gulf crossing — pure nectar fuel.',
      inatTaxonId: 6744,
    },
  },
  {
    id: 'swallows',
    label: 'Swallows — South America → California',
    color: '#9b59b6',
    lineWeight: 2,
    waypoints: [
      [-34.6, -58.4],
      [-20.0, -72.0],
      [5.0, -87.0],
      [20.0, -105.0],
      [28.0, -114.0],
      [33.5, -117.66],
    ],
    endpoints: {
      start: {
        title: 'Southern winter',
        description: 'Pampas & wetlands — mud nests impossible in cold north.',
        role: 'winter',
      },
      end: {
        title: 'Capistrano',
        description: 'Historic mud nests under mission eaves — spring icon.',
        role: 'breeding',
      },
    },
    detail: {
      funFact: 'Cliff swallows navigate by landmarks and may use olfactory cues near colonies.',
      inatTaxonId: 136888,
    },
  },
  {
    id: 'sandhill-crane',
    label: 'Sandhill cranes — Platte → Arctic',
    color: '#f39c12',
    lineWeight: 3,
    waypoints: [
      [40.72, -99.73],
      [44.0, -102.0],
      [52.0, -110.0],
      [62.0, -120.0],
      [68.5, -133.0],
    ],
    endpoints: {
      start: {
        title: 'Platte staging',
        description: 'Critical stopover — cornfields and river shallows refuel thousands.',
        role: 'staging',
      },
      end: {
        title: 'Arctic breeding',
        description: 'Tundra ponds — nesting far from most human activity.',
        role: 'breeding',
      },
    },
    detail: {
      funFact: 'Their ancient bugle calls carry for miles across prairie and tundra.',
      inatTaxonId: 436863,
    },
  },
  {
    id: 'arctic-tern',
    label: 'Arctic terns — pole to pole',
    color: '#1abc9c',
    lineWeight: 3,
    waypoints: [
      [-64.8, -63.5],
      [-50.0, -45.0],
      [-30.0, -35.0],
      [-10.0, -30.0],
      [15.0, -38.0],
      [45.0, -35.0],
      [62.0, -45.0],
      [72.0, -58.0],
      [76.5, -68.7],
    ],
    endpoints: {
      start: {
        title: 'Southern Ocean',
        description: 'Antarctic summer feeding — krill and small fish.',
        role: 'ocean',
      },
      end: {
        title: 'Arctic breeding',
        description: '24-hour daylight nesting — then back south again.',
        role: 'breeding',
      },
    },
    detail: {
      funFact: 'May log 70,000+ km/year — longest migration of any animal.',
      inatTaxonId: 13694,
    },
  },
  {
    id: 'wildebeest',
    label: 'Wildebeest — Serengeti → Mara',
    color: '#27ae60',
    lineWeight: 4,
    waypoints: [
      [-2.38, 34.82],
      [-2.1, 34.92],
      [-1.75, 35.02],
      [-1.45, 35.05],
      [-1.32, 34.98],
    ],
    endpoints: {
      start: {
        title: 'Southern Serengeti',
        description: 'Calving explosion — predators follow the herds.',
        role: 'breeding',
      },
      end: {
        title: 'Maas Mara',
        description: 'River crossings — one of Earth’s great wildlife spectacles.',
        role: 'stopover',
      },
    },
    detail: {
      funFact: 'Over a million animals move in a rough annual circle driven by rain and grass green-up.',
      inatTaxonId: 747652,
    },
  },
  {
    id: 'caribou',
    label: 'Caribou — Alaska',
    color: '#8e44ad',
    lineWeight: 3,
    waypoints: [
      [66.2, -152.8],
      [66.8, -151.5],
      [67.25, -150.2],
      [68.5, -149.0],
      [69.8, -146.5],
      [70.2, -144.8],
    ],
    endpoints: {
      start: {
        title: 'Winter range',
        description: 'Boreal forest edge — lichen under snow.',
        role: 'winter',
      },
      end: {
        title: 'Summer calving',
        description: 'Arctic coastal plain — wolves and bears key predators.',
        role: 'breeding',
      },
    },
    detail: {
      funFact: 'Some herds still travel hundreds of miles — among the longest land migrations.',
      inatTaxonId: 42247,
    },
  },
  {
    id: 'humpback-pac',
    label: 'Humpbacks — Hawaii → Alaska',
    color: '#3498db',
    lineWeight: 5,
    waypoints: [
      [21.05, -157.02],
      [30.0, -165.0],
      [42.0, -158.0],
      [52.0, -152.0],
      [58.5, -152.0],
    ],
    endpoints: {
      start: {
        title: 'Hawaii breeding',
        description: 'Warm shallow water — singing males, calving females.',
        role: 'breeding',
      },
      end: {
        title: 'Alaska feeding',
        description: 'Summer krill and herring — build fat for the return.',
        role: 'ocean',
      },
    },
    detail: {
      funFact: 'North Pacific humpbacks fast almost entirely during the breeding season in Hawaii.',
      inatTaxonId: 85512,
    },
  },
  {
    id: 'humpback-atl',
    label: 'Humpbacks — Caribbean → N. Atlantic',
    color: '#2980b9',
    lineWeight: 4,
    waypoints: [
      [18.35, -68.9],
      [25.0, -68.0],
      [35.0, -55.0],
      [44.5, -63.0],
      [52.0, -58.0],
    ],
    endpoints: {
      start: {
        title: 'Caribbean calving',
        description: 'Silver Bank & similar — warm water nurseries.',
        role: 'breeding',
      },
      end: {
        title: 'North Atlantic feeding',
        description: 'Gulf of St. Lawrence, Norway, Iceland — cold-water prey.',
        role: 'ocean',
      },
    },
    detail: {
      funFact: 'Atlantic humpbacks were severely depleted by whaling; many populations are rebounding.',
      inatTaxonId: 85512,
    },
  },
  {
    id: 'leatherback-pac',
    label: 'Leatherback turtles — Pacific traverse',
    color: '#16a085',
    lineWeight: 3,
    waypoints: [
      [-2.5, 110.0],
      [-6.0, 118.0],
      [-4.0, 132.0],
      [4.0, 150.0],
      [14.0, 172.0],
      [26.0, -178.0],
      [36.0, -155.0],
      [40.0, -135.0],
      [37.5, -122.5],
    ],
    endpoints: {
      start: {
        title: 'Indo-Pacific nesting',
        description: 'Beach nesting — largest living turtle.',
        role: 'breeding',
      },
      end: {
        title: 'California Current',
        description: 'Jellyfish foraging — multi-year oceanic loops.',
        role: 'ocean',
      },
    },
    detail: {
      funFact: 'Leatherbacks routinely cross entire ocean basins and dive past 1,000 m for prey.',
      inatTaxonId: 39868,
    },
  },
  {
    id: 'salmon-pac',
    label: 'Pacific salmon — ocean → rivers',
    color: '#c0392b',
    lineWeight: 3,
    waypoints: [
      [48.5, -128.0],
      [48.2, -124.5],
      [46.25, -124.05],
      [45.65, -121.75],
      [45.6, -121.2],
    ],
    endpoints: {
      start: {
        title: 'North Pacific',
        description: 'Years growing at sea — imprint home stream chemistry.',
        role: 'ocean',
      },
      end: {
        title: 'Columbia / Snake',
        description: 'Spawning gravels — semelparous death completes the cycle.',
        role: 'breeding',
      },
    },
    detail: {
      funFact: 'Pacific salmon are a cornerstone species — bears, forests, and fisheries depend on the nutrient pulse.',
      inatTaxonId: 47551,
    },
  },
  {
    id: 'godwit',
    label: 'Bar-tailed godwit — Alaska → New Zealand',
    color: '#d35400',
    lineWeight: 2,
    waypoints: [
      [61.2, -165.5],
      [52.0, -172.0],
      [40.0, -178.0],
      [25.0, -172.0],
      [8.0, -162.0],
      [-12.0, -155.0],
      [-30.0, -148.0],
      [-42.0, 172.0],
      [-36.85, 174.75],
    ],
    endpoints: {
      start: {
        title: 'Alaska breeding',
        description: 'Yukon–Kuskokwim tundra — hyperphagia before departure.',
        role: 'breeding',
      },
      end: {
        title: 'New Zealand',
        description: 'Nonstop 11,000+ km — longest known nonstop bird flight.',
        role: 'winter',
      },
    },
    detail: {
      funFact: 'Satellite-tagged birds have flown >11,000 km without landing or feeding.',
      inatTaxonId: 3455,
    },
  },
  {
    id: 'eel-european',
    label: 'European eels — Sargasso → Europe',
    color: '#6c3483',
    lineWeight: 2,
    waypoints: [
      [26.0, -68.0],
      [32.0, -55.0],
      [40.0, -25.0],
      [50.0, -5.0],
      [52.5, 5.5],
    ],
    endpoints: {
      start: {
        title: 'Sargasso Sea',
        description: 'Spawning still never fully witnessed — leptocephalus larvae drift east.',
        role: 'breeding',
      },
      end: {
        title: 'European rivers',
        description: 'Glass eels ascend — live decades before silver eels return.',
        role: 'stopover',
      },
    },
    detail: {
      funFact: 'The full life cycle was a mystery for centuries; eels remain critically endangered.',
      inatTaxonId: 924916,
    },
  },
  {
    id: 'red-crab',
    label: 'Christmas Island red crabs',
    color: '#e74c3c',
    lineWeight: 2,
    waypoints: [
      [-10.45, 105.62],
      [-10.5, 105.68],
      [-10.54, 105.72],
    ],
    endpoints: {
      start: {
        title: 'Island forest',
        description: 'Moist inland burrows — most of the year.',
        role: 'winter',
      },
      end: {
        title: 'Coast spawning',
        description: 'Moon-triggered march — roads closed for crab tide.',
        role: 'breeding',
      },
    },
    detail: {
      funFact: 'Tens of millions of red crabs carpet the island — a rare terrestrial mass migration.',
      inatTaxonId: 492026,
    },
  },
  {
    id: 'darner-dragonfly',
    label: 'Green darner — eastern flyway',
    color: '#58d68d',
    lineWeight: 1,
    waypoints: [
      [43.0, -87.9],
      [41.5, -88.2],
      [39.8, -89.6],
      [35.0, -90.0],
      [30.5, -87.5],
      [29.0, -90.0],
    ],
    endpoints: {
      start: {
        title: 'Great Lakes summer',
        description: 'Large mosaic hawker — breeding in north.',
        role: 'breeding',
      },
      end: {
        title: 'Gulf Coast',
        description: 'Multigenerational relay — offspring return north in spring.',
        role: 'winter',
      },
    },
    detail: {
      funFact: 'One of the first insects proven to migrate hundreds of miles like birds.',
      inatTaxonId: 81979,
    },
  },
];

export const ALL_MIGRATION_GROUP_IDS = MIGRATION_ROUTE_GROUPS.map((g) => g.id);

export function getMigrationGroupById(
  id: string
): MigrationRouteGroup | undefined {
  return MIGRATION_ROUTE_GROUPS.find((g) => g.id === id);
}

/** Bounding box around waypoints with padding (degrees). */
export function routeWaypointsBounds(
  waypoints: [number, number][],
  pad = 2
): { nelat: number; nelng: number; swlat: number; swlng: number } {
  let nelat = -90,
    swlat = 90,
    nelng = -180,
    swlng = 180;
  for (const [lat, lng] of waypoints) {
    nelat = Math.max(nelat, lat);
    swlat = Math.min(swlat, lat);
    nelng = Math.max(nelng, lng);
    swlng = Math.min(swlng, lng);
  }
  return {
    nelat: Math.min(85, nelat + pad),
    swlat: Math.max(-85, swlat - pad),
    nelng: Math.min(180, nelng + pad),
    swlng: Math.max(-180, swlng - pad),
  };
}
